/**
 * Hierarchical navigation config for card-based dashboard.
 * One card per section on home; section hubs show children as cards.
 */

export interface NavItem {
  path: string
  i18nKey: string
  icon?: string
  /** If set, this is a section: home shows one card linking to path; hub page shows children as cards */
  children?: NavItem[]
}

/** Top-level sections shown as cards on home (when logged in). */
export const mainNavSections: NavItem[] = [
  { path: '/home', i18nKey: 'menu.home', icon: 'HomeFilled' },
  { path: '/excelEntryManager', i18nKey: 'menu.excelEntryManager', icon: 'Document' },
  { path: '/irrigation-dashboard', i18nKey: 'menu.irrigationDashboard.title', icon: 'Odometer' },
  { path: '/colheita', i18nKey: 'menu.colheita', icon: 'Calendar' },
  { path: '/payloads', i18nKey: 'menu.payloads', icon: 'Document' },
  { path: '/map', i18nKey: 'menu.map', icon: 'Location' },
  {
    path: '/apple-varieties',
    i18nKey: 'menu.appleVarieties.title',
    icon: 'Apple',
    children: [
      { path: '/gala-apples', i18nKey: 'menu.appleVarieties.gala' },
      { path: '/fuji-apples', i18nKey: 'menu.appleVarieties.fuji' },
    ],
  },
  {
    path: '/farmonaut',
    i18nKey: 'menu.farmonaut.title',
    icon: 'Compass',
    children: [
      { path: '/farmonaut/plantations', i18nKey: 'menu.farmonaut.plantations' },
      { path: '/farmonaut/fields', i18nKey: 'menu.farmonaut.fields' },
      { path: '/farmonaut/upload', i18nKey: 'menu.farmonaut.upload' },
      { path: '/farmonaut/upload-file', i18nKey: 'menu.farmonaut.uploadFile' },
      { path: '/farmonaut/auto-fix', i18nKey: 'menu.farmonaut.autoFix' },
      { path: '/farmonaut/validate', i18nKey: 'menu.farmonaut.validate' },
      { path: '/farmonaut/transform', i18nKey: 'menu.farmonaut.transform' },
      { path: '/farmonaut/comparison', i18nKey: 'menu.farmonaut.comparison' },
      { path: '/farmonaut/assignment', i18nKey: 'menu.farmonaut.assignment' },
      { path: '/farmonaut/create-farmonaut', i18nKey: 'menu.farmonaut.createFarmonautFields' },
      { path: '/farmonaut/health', i18nKey: 'menu.farmonaut.health' },
    ],
  },
  {
    path: '/playground',
    i18nKey: 'menu.playground.title',
    icon: 'Grid',
    children: [
      { path: '/table', i18nKey: 'menu.playground.table' },
      { path: '/datatable', i18nKey: 'menu.playground.datatable' },
      { path: '/upload', i18nKey: 'menu.playground.upload' },
      { path: '/fibuInputChange', i18nKey: 'menu.playground.fibuInputChange' },
      { path: '/segmented', i18nKey: 'menu.playground.segmented' },
      { path: '/timeline', i18nKey: 'menu.playground.timeline' },
      { path: '/setor', i18nKey: 'menu.playground.setor' },
      { path: '/sample-usage', i18nKey: 'menu.playground.sampleUsage' },
      { path: '/playground/create-plantation', i18nKey: 'menu.playground.createPlantation' },
    ],
  },
  {
    path: '/tests',
    i18nKey: 'menu.tests.title',
    icon: 'Collection',
    children: [
      { path: '/i18n', i18nKey: 'menu.tests.i18n' },
    ],
  },
  {
    path: '/test',
    i18nKey: 'menu.test.title',
    icon: 'Monitor',
    children: [
      { path: '/test/formily', i18nKey: 'menu.test.formily' },
    ],
  },
]

/** Items shown as cards when logged out */
export const publicNavItems: NavItem[] = [
  { path: '/welcome', i18nKey: 'menu.welcome', icon: 'Star' },
  { path: '/login', i18nKey: 'menu.login', icon: 'User' },
  { path: '/about', i18nKey: 'menu.about', icon: 'InfoFilled' },
]

/** Section hub path -> children (for SectionCardDashboard) */
export const sectionChildrenByPath: Record<string, NavItem[]> = {}
mainNavSections.forEach((item) => {
  if (item.children?.length) {
    sectionChildrenByPath[item.path] = item.children
  }
})
